import * as T from 'three'
import type { CharacterId } from './config'
import { skinFor, type SkinId } from './progress'
export type ActorId = CharacterId | 'mother' | 'leopard'

// User-selected Sprite V2 atlases: 8 columns, 11 rows, 192 × 208 per frame.
// Keep original pixels and alpha; no generated substitute character geometry.
const files: Record<ActorId, string> = { cow: 'characters/cow', frog: 'characters/frog', mouse: 'characters/kangaroo', mother:'companions/mother',leopard:'companions/leopard' }
const frameCounts = [7, 8, 8, 4, 5, 8, 6, 6, 6, 8, 8]
const loader = new T.TextureLoader()
const sheets = new Map<string, Promise<T.Texture>>()
function sheet(url: string) {
  let promise = sheets.get(url)
  if (!promise) {
    promise = loader.loadAsync(url).then(texture=>{
      texture.colorSpace=T.SRGBColorSpace;texture.minFilter=T.LinearFilter;texture.magFilter=T.LinearFilter;texture.generateMipmaps=false;return texture
    })
    sheets.set(url,promise)
    void promise.catch(()=>{if(sheets.get(url)===promise)sheets.delete(url)})
  }
  return promise
}
export class SpriteActor {
  readonly root = new T.Group()
  private material = new T.SpriteMaterial({ transparent: true, depthWrite: false, alphaTest: .025, toneMapped: false })
  private sprite = new T.Sprite(this.material)
  private costumeMaterial = new T.MeshBasicMaterial({ transparent:true,depthWrite:false,alphaTest:.025,toneMapped:false })
  // A subdivided billboard lets a single costume illustration move like a small paper puppet.
  // Original atlases continue to use their authored frame animation in `sprite`.
  private costumeGeometry = new T.PlaneGeometry(1, 1, 24, 32)
  private costumeMesh = new T.Mesh(this.costumeGeometry, this.costumeMaterial)
  private costumeBase = this.costumeGeometry.attributes.position.array.slice() as Float32Array
  private texture?: T.Texture
  private id!: ActorId
  private skin:SkinId = 'original'
  assetStatus:'loading'|'ready'|'failed'='loading'
  private assetKey='';private costume=false;private aspect=192/208
  private atlasWidth=1536;private atlasHeight=2288
  private state = ''; private stateStart = 0
  constructor(id: ActorId) {
    this.sprite.center.set(.5, .08)
    this.root.add(this.sprite)
    this.costumeMesh.visible = false
    this.costumeMesh.frustumCulled = false
    this.root.add(this.costumeMesh)
    const shadow = new T.Mesh(new T.CircleGeometry(.77, 48), new T.MeshBasicMaterial({ color: '#3d3628', transparent: true, opacity: .2, depthWrite: false }))
    shadow.rotation.x = -Math.PI / 2; shadow.scale.y = .7; shadow.position.y = .025
    this.root.add(shadow)
    this.setCharacter(id)
  }
  setCharacter(id: ActorId) {
    if (this.id === id) return
    this.id=id;this.state='';this.loadAppearance()
  }
  private loadAppearance() {
    const skin=skinFor(this.skin)
    const costume=skin.character===this.id&&skin.image!==null
    const cutout=costume&&skin.atlas===null
    const url=costume?(skin.atlas??skin.image!):`/assets/${files[this.id]}.webp`
    if(this.assetKey===url)return
    this.assetKey=url;this.assetStatus='loading';this.sprite.visible=false;this.costumeMesh.visible=false
    void sheet(url).then(source=>{
      if(this.assetKey!==url)return
      this.texture?.dispose();this.texture=source.clone()
      this.costume=cutout
      const image=source.image as {width:number;height:number}
      this.atlasWidth=image.width;this.atlasHeight=image.height
      this.aspect=cutout?image.width/image.height:(image.width/8)/(image.height/11)
      this.texture.repeat.set(cutout?1:1/8-2/image.width,cutout?1:1/11-2/image.height)
      this.texture.offset.set(cutout?0:1/image.width,cutout?0:1-1/11+1/image.height)
      this.material.map=this.texture;this.costumeMaterial.map=this.texture
      this.material.needsUpdate=true;this.costumeMaterial.needsUpdate=true
      this.state='';this.sprite.visible=!cutout;this.costumeMesh.visible=cutout;this.assetStatus='ready'
    }).catch(error=>{if(this.assetKey===url){this.assetStatus='failed';console.error('角色贴图加载失败',url,error)}})
  }
  setSkin(id:SkinId) {
    if(this.skin===id)return
    this.skin=id;this.loadAppearance()
  }
  setDimmed(dimmed: boolean) { this.material.opacity = this.costumeMaterial.opacity = dimmed ? .45 : 1 }
  update(time: number, speed: number, facingX: number, attack: number, skill: number, charge: number, failed: boolean, cameraQuaternion?:T.Quaternion) {
    if(!this.texture||(!this.sprite.visible&&!this.costumeMesh.visible))return
    let row = 0; let fps = 5
    const active = attack > 0 || skill > 0
    if (failed) { row = 5; fps = 5 }
    else if (this.id === 'mother' && active) { row=4;fps=8 }
    else if (this.id === 'leopard' && active) { row=facingX<-.1?2:1;fps=14 }
    else if (this.id === 'frog' && active) { row = 4; fps = 8 }
    else if (this.id === 'mouse' && active) { row = 4; fps = 12 }
    else if (this.id === 'cow' && skill > 0) { row = facingX < -.1 ? 2 : 1; fps = 15 }
    else if (this.id === 'cow' && charge > 0) { row = 7; fps = 6 }
    else if (this.id === 'cow' && attack > 0) { row = 3; fps = 9 }
    else if (speed > .35) { row = facingX < -.1 ? 2 : 1; fps = Math.min(12, 7 + speed * .5) }
    const state = `${this.id}:${row}`
    if (state !== this.state || time < this.stateStart) { this.state = state; this.stateStart = time }
    const elapsed = Math.max(0, time - this.stateStart)
    // Attacks start with their anticipation frame, then the full authored motion.
    const frame = failed ? Math.min(7, Math.floor(elapsed * fps)) : Math.floor(elapsed * fps) % frameCounts[row]
    if(!this.costume)this.texture.offset.set(frame/8+1/this.atlasWidth,1-(row+1)/11+1/this.atlasHeight)
    const height = this.id === 'cow' ? 3.55 : this.id === 'frog' ? 3.55 : 3.45
    const hop = this.id === 'mouse' && active ? Math.sin((1 - attack) * Math.PI) * .65 : 0
    const laugh = this.id === 'frog' && active ? Math.sin(time * 22) * (this.costume?.075:.035) : 0
    const lunge = this.id === 'cow' && attack > 0 ? Math.sin((1 - attack) * Math.PI) : 0
    const stride=this.costume&&speed>.35?Math.abs(Math.sin(time*(this.id==='cow'?16:11)))*.13:0
    this.sprite.position.set(lunge * facingX * (this.costume?.4:.17), .05 + hop + Math.abs(laugh)+stride, 0)
    this.sprite.scale.set(height * this.aspect * (1 + laugh), height * (1 - laugh), 1)
    // A short lean is a punch / leap accent, never a looping body rotation.
    const lean=this.id === 'cow' ? -facingX * (lunge * .07+(skill>0?.16:0)) : this.id === 'mouse' && active ? -facingX * .12 : 0
    // SpriteMaterial rotates UVs; the costume uses a real subdivided plane, so rotate its mesh instead.
    this.material.rotation=this.costume?0:lean
    if(this.costumeMesh.visible){
      this.animateCostume(time,speed,attack,skill,failed,active)
      this.costumeMesh.position.set(this.sprite.position.x,this.sprite.position.y+height*(1-laugh)*.5,0)
      this.costumeMesh.scale.set(height*this.aspect*(1+laugh),height*(1-laugh),1)
      if(cameraQuaternion){this.costumeMesh.quaternion.copy(cameraQuaternion);this.costumeMesh.rotateZ(lean)}
    }
  }

  private animateCostume(time:number,speed:number,attack:number,skill:number,failed:boolean,active:boolean){
    const attr=this.costumeGeometry.attributes.position
    const uv=this.costumeGeometry.attributes.uv
    const phase=time*(speed>.35?11:2.8)
    const action=(time-this.stateStart)*(this.id==='cow'?15:12)
    const walking=speed>.35&&!active&&!failed
    const pumping=active&&!failed
    const bellows=this.id==='frog'&&pumping
    const charging=this.id==='cow'&&(attack>0||skill>0)
    const hopping=this.id==='mouse'&&pumping
    const strength=failed?.75:1
    const gauss=(u:number,v:number,cx:number,cy:number,sx:number,sy:number)=>Math.exp(-(((u-cx)/sx)**2+((v-cy)/sy)**2)*2)
    for(let i=0;i<attr.count;i++){
      const u=uv.getX(i),v=1-uv.getY(i),j=i*3
      let dx=0,dy=0
      // Soft breathing keeps the whole cutout alive while idle.
      dy+=Math.sin(time*3.2)*.008*gauss(u,v,.5,.48,.42,.45)
      if(this.id==='frog'){
        const swing=bellows?Math.sin(action):walking?Math.sin(phase):Math.sin(time*1.4)*.16
        // Each hand lifts independently during the laugh; the feet bounce under the belly.
        dx+=swing*.045*gauss(u,v,.28,.57,.19,.18)-swing*.045*gauss(u,v,.72,.57,.19,.18)
        dy-=Math.max(0,swing)*.035*(gauss(u,v,.28,.57,.2,.18)+gauss(u,v,.72,.57,.2,.18))
        if(bellows)dy+=Math.abs(Math.sin(action*1.2))*.04*gauss(u,v,.5,.84,.28,.15)
        if(walking){dy+=Math.sin(phase*2)*.045*(gauss(u,v,.36,.92,.13,.12)+gauss(u,v,.64,.92,.13,.12))}
        if(failed){dy+=Math.sin(action*1.5)*.018*gauss(u,v,.5,.17,.35,.22)}
      }else if(this.id==='cow'){
        const swing=charging?Math.sin(action):walking?Math.sin(phase):Math.sin(time*1.2)*.12
        dx+=swing*.055*gauss(u,v,.2,.56,.15,.18)-swing*.055*gauss(u,v,.8,.56,.15,.18)
        dy-=Math.max(0,swing)*.025*(gauss(u,v,.2,.56,.16,.2)+gauss(u,v,.8,.56,.16,.2))
        // The long robe hem follows the upright body instead of remaining a rigid card.
        dx+=Math.sin(time*4+v*9)*(.012+(charging?.025:0))*gauss(u,v,.5,.88,.52,.18)
        if(walking||charging)dy+=Math.sin(phase*2)*.025*(gauss(u,v,.34,.96,.2,.07)+gauss(u,v,.66,.96,.2,.07))
        if(failed)dx+=Math.sin(action*1.2)*.025*gauss(u,v,.5,.52,.4,.42)
      }else{
        const swing=hopping?Math.sin(action):walking?Math.sin(phase):Math.sin(time*1.5)*.12
        dx+=swing*.045*gauss(u,v,.28,.56,.15,.15)-swing*.045*gauss(u,v,.72,.56,.15,.15)
        // Hop lifts the ears and spreads the skirt; walking alternates the feet.
        dy-=Math.max(0,swing)*.04*(gauss(u,v,.4,.1,.1,.14)+gauss(u,v,.62,.1,.11,.14))
        const skirt=hopping?Math.abs(Math.sin(action)):.25+Math.sin(time*2)*.08
        dx+=Math.sign(u-.5)*skirt*.035*gauss(u,v,.5,.75,.43,.12)
        if(hopping)dy+=Math.abs(Math.sin(action))*.055*gauss(u,v,.5,.91,.3,.1)
        if(walking)dy+=Math.sin(phase*2)*.05*(gauss(u,v,.34,.95,.16,.09)+gauss(u,v,.66,.95,.16,.09))
        dx+=Math.sin(time*4)*.035*gauss(u,v,.93,.7,.1,.2)
        if(failed)dy+=Math.sin(action)*.02*gauss(u,v,.5,.26,.3,.3)
      }
      attr.array[j]=this.costumeBase[j]+dx*strength
      attr.array[j+1]=this.costumeBase[j+1]+dy*strength
      attr.array[j+2]=this.costumeBase[j+2]
    }
    attr.needsUpdate=true
    this.costumeGeometry.computeVertexNormals()
  }
}
